// Nails by Asha — the arithmetic behind the books. Pure functions, no React, no
// network, so they can be run and checked in Node.
//
// The business is a sole trader (Asha's own ABN) that is NOT registered for GST.
// That is not a detail — it is the rule this whole file obeys:
//   • she charges no GST, so income is simply what the client paid;
//   • she claims no input credits, so an expense is the GROSS amount on the docket.
// Nothing here ever divides by 11, and there is no GST line to report.

// She started taking clients on 1 July 2026. Anything spent before that is a lead-up
// cost sitting in the PREVIOUS financial year — kept at its true date, reported
// separately, and left for the accountant to treat. The engine does not decide it.
export const TRADING_START = '2026-07-01';

// Registration for GST becomes compulsory once turnover reaches this in a 12-month
// period. The page shows the number so it is never a surprise; the accountant advises.
export const GST_THRESHOLD = 75000;

export const EXPENSE_CATEGORIES = [
  'Stock & supplies',
  'Tools & equipment',
  'Training & courses',
  'Insurance',
  'Marketing & website',
  'Phone & internet',
  'Travel & parking',
  'Rent / booth fee',
  'Bank & fees',
  'Other',
];

export const PAYMENT_METHODS = [
  { value: 'cash', label: 'Cash' },
  { value: 'transfer', label: 'Bank transfer' },
  { value: 'card', label: 'Card' },
  { value: 'other', label: 'Other' },
];

// ── money ─────────────────────────────────────────────────────────────────────
// Sum in cents and come back to dollars once, so a long ledger cannot drift by a
// cent the way repeated float addition does.
export const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
export const sum = (rows, pick) => round2(rows.reduce((t, r) => t + Math.round((Number(pick(r)) || 0) * 100), 0) / 100);
export const money = (n) => (Number(n) || 0).toLocaleString('en-AU', { style: 'currency', currency: 'AUD' });

// ── financial years (Australian: 1 July → 30 June) ────────────────────────────
const d = (s) => (s || '').toString().slice(0, 10);

// The FY a date falls in, named by its starting year: 2026-08-14 → '2026-27'.
export function fyOf(dateStr) {
  const s = d(dateStr);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return '';
  const year = Number(s.slice(0, 4));
  const month = Number(s.slice(5, 7));
  const start = month >= 7 ? year : year - 1;
  return `${start}-${String((start + 1) % 100).padStart(2, '0')}`;
}

export function fyRange(fy) {
  const start = Number((fy || '').toString().slice(0, 4));
  if (!start) return { from: '', to: '' };
  return { from: `${start}-07-01`, to: `${start + 1}-06-30` };
}

export const fyLabel = (fy) => {
  const { from, to } = fyRange(fy);
  return from ? `FY ${fy} (1 Jul ${from.slice(0, 4)} – 30 Jun ${to.slice(0, 4)})` : '';
};

// LOCAL calendar date, never toISOString(): Sydney is UTC+10/+11, so on the morning of
// 1 July toISOString() still says 30 June and this would report the OUTGOING financial
// year on the very day the new one starts.
export const currentFy = (today = new Date()) =>
  fyOf(typeof today === 'string' ? today
    : `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`);

// A cost incurred before she started trading. Derived from the date rather than stored
// as a flag, so it can never disagree with the date on the receipt.
export const isPreStart = (dateStr) => {
  const s = d(dateStr);
  return !!s && s < TRADING_START;
};

const inRange = (dateStr, from, to) => {
  const s = d(dateStr);
  if (!s) return false;
  if (from && s < from) return false;
  if (to && s > to) return false;
  return true;
};

// ── grouping helpers ──────────────────────────────────────────────────────────
function totalsBy(rows, keyOf, amountOf) {
  const map = new Map();
  rows.forEach((r) => {
    const key = keyOf(r) || 'Uncategorised';
    const cur = map.get(key) || { key, total: 0, count: 0 };
    cur.total += Math.round((Number(amountOf(r)) || 0) * 100);
    cur.count += 1;
    map.set(key, cur);
  });
  return [...map.values()]
    .map((g) => ({ ...g, total: round2(g.total / 100) }))
    .sort((a, b) => b.total - a.total);
}

const monthOf = (dateStr) => d(dateStr).slice(0, 7);

// ── the P&L ───────────────────────────────────────────────────────────────────
// `period` is { from, to } inclusive; omit either end to leave it open. Rows outside
// the period are excluded entirely — including pre-start expenses, which is why they
// get their own schedule rather than quietly inflating a trading year.
export function summarise(income = [], expenses = [], period = {}) {
  const { from = '', to = '' } = period;
  const inc = income.filter((r) => inRange(r.jobDate, from, to));
  const exp = expenses.filter((r) => inRange(r.spendDate, from, to));

  const incomeTotal = sum(inc, (r) => r.amount);
  const expenseTotal = sum(exp, (r) => r.amount);

  // Every month that has any activity, oldest first — the shape of the year.
  const months = [...new Set([...inc.map((r) => monthOf(r.jobDate)), ...exp.map((r) => monthOf(r.spendDate))])]
    .filter(Boolean).sort();

  return {
    period: { from, to },
    income: {
      total: incomeTotal,
      count: inc.length,
      byMethod: totalsBy(inc, (r) => r.paymentMethod || 'cash', (r) => r.amount),
      rows: inc,
    },
    expenses: {
      total: expenseTotal,
      count: exp.length,
      byCategory: totalsBy(exp, (r) => r.category, (r) => r.amount),
      // The list that actually saves her in July: money claimed with no docket behind it.
      missingReceipts: exp.filter((r) => !r.hasReceipt),
      rows: exp,
    },
    net: round2(incomeTotal - expenseTotal),
    // Average take per job — the number she will actually care about week to week.
    averageJob: inc.length ? round2(incomeTotal / inc.length) : 0,
    byMonth: months.map((m) => {
      const mi = sum(inc.filter((r) => monthOf(r.jobDate) === m), (r) => r.amount);
      const me = sum(exp.filter((r) => monthOf(r.spendDate) === m), (r) => r.amount);
      return { month: m, income: mi, expenses: me, net: round2(mi - me) };
    }),
  };
}

// Costs incurred before 1 Jul 2026 — the schedule handed to the accountant whole,
// with no opinion attached about how they should be treated.
export const preStartExpenses = (expenses = []) =>
  expenses.filter((r) => isPreStart(r.spendDate))
    .slice()
    .sort((a, b) => d(a.spendDate).localeCompare(d(b.spendDate)));

// Turnover so far this financial year, against the registration threshold.
export function gstWatch(income = [], fy = currentFy()) {
  const { from, to } = fyRange(fy);
  const turnover = sum(income.filter((r) => inRange(r.jobDate, from, to)), (r) => r.amount);
  return {
    fy,
    turnover,
    threshold: GST_THRESHOLD,
    remaining: round2(GST_THRESHOLD - turnover),
    percent: Math.min(100, Math.round((turnover / GST_THRESHOLD) * 100)),
    reached: turnover >= GST_THRESHOLD,
  };
}

// Every FY that has a row in it, newest first, so the period picker only ever offers
// years that exist. The current FY is always included even when it is still empty.
export function availableFys(income = [], expenses = [], today = new Date()) {
  const set = new Set([currentFy(today)]);
  income.forEach((r) => { const f = fyOf(r.jobDate); if (f) set.add(f); });
  expenses.forEach((r) => { const f = fyOf(r.spendDate); if (f) set.add(f); });
  return [...set].sort().reverse();
}
